import React, { useState, useEffect } from "react";
import {
  GoogleMap,
  Marker,
  Autocomplete,
  InfoWindow,
  Circle,
} from "@react-google-maps/api";
import { useGoogleMaps } from "./useGoogleMaps";
import { IconButton } from "@mui/material";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Popover,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import ArthaTextField from "../../fields/ArthaTextField";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import ArthaCancelButton from "../../fields/ArthaCancelButton";
import { Pencil, Trash, MapPin, Info } from "lucide-react"; // Add MapPin and Info import
import location from "../../Assets/location.png";
import {
  saveLocationAPI,
  fetchLocationApi,
  updateLocationAPI,deleteWorkScheduleAPI,
  fetchSubOrgDropdown,
} from "../../actioncreators/actioncreators";
import { useSelector } from "react-redux";
import { useSuspension } from "../../contexts/SuspensionContext";
import "./dashboard.css";

const AddWorkLocation = () => {
  const { isLoaded: isMapsLoaded } = useGoogleMaps();

  // Suspension check
  const { isSuspended } = useSuspension();
  // Super Admin org filtering support
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const loginResponse = useSelector((state) => state.loginResponse);
  const isSuperAdmin = loginResponse?.role_name?.toUpperCase() === 'SUPER_ADMIN' || loginResponse?.roleName?.toUpperCase() === 'SUPER_ADMIN';
  const isAdmin = loginResponse?.role_name?.toUpperCase() === 'ADMIN' || loginResponse?.roleName?.toUpperCase() === 'ADMIN';
  const userRole = loginResponse?.role_name || loginResponse?.roleName || '';
  const superAdminSelectedSubOrgId = superAdminSelectedOrgData?.type === 'sub_org' ? superAdminSelectedOrgData?.sub_org_id : null;

  // Sub-organization state
  const [subOrganizations, setSubOrganizations] = useState([]);
  const [mapCenter, setMapCenter] = useState({
    lat: 20.5937,
    lng: 78.9629,
  });
  const [zoomLevel, setZoomLevel] = useState(2); // State to manage zoom level
  const [autocomplete, setAutocomplete] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null); // Location details
  const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false);
  const [isInfoWindowOpen1, setIsInfoWindowOpen1] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false); // For controlling dialog visibility
  const [selectedLocationId, setSelectedLocationId] = useState(null); // State to track selected location
  const [isEditing, setIsEditing] = useState(false); // State to track selected location

  const [formData, setFormData] = useState({
    locationName: "",
    coordinates: "",
    locationAddress: "",
    sub_org_id: "",
  });
  const [viewListData, setViewListData] = useState(false);
  const [locPage, setLocPage] = useState(1);
  const locPageSize = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState(""); // State for search input
  const [locationKey, setLocationKey] = useState(0); // Unique key for forcing marker/circle re-render
  const [infoAnchorEl, setInfoAnchorEl] = useState(null); // Anchor element for info popover
  const [infoLocation, setInfoLocation] = useState(null); // Location to show in info popover

  const handleOnLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  // Function to check if input is coordinates (lat, lng format)
  const isCoordinates = (input) => {
    // Match patterns like "12.345, 67.890" or "12.345,67.890" or "12.345 67.890"
    const coordPattern = /^\s*(-?\d+\.?\d*)\s*[,\s]\s*(-?\d+\.?\d*)\s*$/;
    return coordPattern.test(input);
  };

  // Function to parse coordinates from input
  const parseCoordinates = (input) => {
    const coordPattern = /^\s*(-?\d+\.?\d*)\s*[,\s]\s*(-?\d+\.?\d*)\s*$/;
    const match = input.match(coordPattern);
    if (match) {
      return {
        lat: parseFloat(match[1]),
        lng: parseFloat(match[2])
      };
    }
    return null;
  };

  // Handle search by coordinates
  const handleCoordinateSearch = (lat, lng) => {
    // Validate coordinates
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      toast.error("Invalid coordinates. Latitude must be between -90 and 90, Longitude between -180 and 180.");
      return;
    }

    setLocationKey(prev => prev + 1); // Increment for new location
    
    // Use Geocoder to get address from coordinates
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = {
          name: results[0].formatted_address.split(",")[0] || "Selected Location",
          address: results[0].formatted_address,
          lat: lat,
          lng: lng,
        };
        setSelectedLocation(location);
        setMapCenter({ lat, lng });
        setZoomLevel(12);
        setIsInfoWindowOpen(true);
        setIsInfoWindowOpen1(false);
      } else {
        // If geocoding fails, still show marker with coordinates
        const location = {
          name: "Selected Location",
          address: `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`,
          lat: lat,
          lng: lng,
        };
        setSelectedLocation(location);
        setMapCenter({ lat, lng });
        setZoomLevel(12);
        setIsInfoWindowOpen(true);
        setIsInfoWindowOpen1(false);
      }
    });
  };

  // Handle Enter key press for coordinate search
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      const input = searchInput.trim();
      if (isCoordinates(input)) {
        e.preventDefault();
        const coords = parseCoordinates(input);
        if (coords) {
          handleCoordinateSearch(coords.lat, coords.lng);
        }
      }
      // If not coordinates, let Autocomplete handle it
    }
  };

  // Handle search button click
  const handleSearchClick = () => {
    const input = searchInput.trim();
    if (isCoordinates(input)) {
      const coords = parseCoordinates(input);
      if (coords) {
        handleCoordinateSearch(coords.lat, coords.lng);
      }
    }
  };

  useEffect(() => {
    fetchMembers();
    loadSubOrganizations();
    // Admin drilling into/out of a sub-org via the header dropdown only
    // changes superAdminSelectedSubOrgId (superAdminSelectedOrgId stays the
    // Admin's own org_id throughout) - without this dependency, switching
    // sub-orgs never re-fetched and the page kept showing stale data.
  }, [superAdminSelectedOrgId, superAdminSelectedSubOrgId]);

  // Load sub-organizations for dropdown
  const loadSubOrganizations = async () => {
    try {
      // Scope the dropdown to the org a Super Admin has selected; without this the
      // backend falls back to the Super Admin's own JWT org and the list always
      // shows that org's sub-orgs regardless of the selection.
      const response = await fetchSubOrgDropdown(isSuperAdmin ? superAdminSelectedOrgId : null);
      // Always reset the list, otherwise a failed or empty reload leaves the
      // previously selected org's sub-orgs showing in the dropdown.
      setSubOrganizations(
        response?.data?.businessStatusCode === 2 ? (response.data.data || []) : []
      );
    } catch (error) {
      console.error('Failed to load sub-organizations:', error);
      setSubOrganizations([]);
    }
  };

  const fetchMembers = async () => {
    const requestBody = {
      entity_type: "location",
      pagenumber: 1,
      parameter: [
        {
          columnName: "status",
          condition: "=",
          value: "ACTIVE",
        },
      ],
    };

    // Add org_id for Super Admin viewing specific organization
    if (isSuperAdmin && superAdminSelectedOrgId) {
      requestBody.org_id = superAdminSelectedOrgId;
      if (superAdminSelectedSubOrgId) {
        requestBody.sub_org_id = superAdminSelectedSubOrgId;
      }
    } else if (isAdmin && superAdminSelectedSubOrgId) {
      // Admin drilled into a sub-org via the header dropdown. No org_id
      // override needed/valid for Admin (their own org is implicit via the
      // JWT header) - only sub_org_id, validated server-side against the
      // caller's own accessible scope (see ViewEntityList).
      requestBody.sub_org_id = superAdminSelectedSubOrgId;
    }

    try {
      const response = await fetchLocationApi(requestBody); // Fetch from your API

      const { businessStatusCode, viewListData } = response;
      setViewListData(viewListData);
      console.log("viewListData", viewListData);
      // Check if the response contains the data as expected
      if (businessStatusCode === 2 && viewListData) {
      } else {
        setError("No members found. Please add members to get started.");
      }
    } catch (err) {
      setError(err.message);
    }
  };
  const handlePlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        setLocationKey(prev => prev + 1); // Increment for new location
        const location = {
          name: place.name,
          address: place.formatted_address,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setSelectedLocation(location);
        setMapCenter({ lat: location.lat, lng: location.lng });
        setZoomLevel(12);
        setIsInfoWindowOpen(true); // Show the info window
      }
    }
  };

  // Handle map click event to add location
  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    // Clear previously searched/selected location and close all info windows
    setSelectedLocation(null);
    setIsInfoWindowOpen(false);
    setIsInfoWindowOpen1(false);
    setSearchInput(""); // Clear search input

    // Small delay to ensure previous marker and circle are cleared before adding new one
    setTimeout(() => {
      // Increment location key to force re-render
      setLocationKey(prev => prev + 1);
      
      // Use Geocoder to get address from coordinates
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = {
            name: results[0].formatted_address.split(",")[0] || "Selected Location",
            address: results[0].formatted_address,
            lat: lat,
            lng: lng,
            isNewLocation: true, // Flag to indicate this is a new location from map click
          };
          setSelectedLocation(location);
          setMapCenter({ lat, lng });
          setZoomLevel(12);
          setIsInfoWindowOpen(true);
          setIsInfoWindowOpen1(false);
        } else {
          // If geocoding fails, still show marker with coordinates
          const location = {
            name: "Selected Location",
            address: `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`,
            lat: lat,
            lng: lng,
            isNewLocation: true, // Flag to indicate this is a new location from map click
          };
          setSelectedLocation(location);
          setMapCenter({ lat, lng });
          setZoomLevel(12);
          setIsInfoWindowOpen(true);
          setIsInfoWindowOpen1(false);
        }
      });
    }, 100);
  };

  const handleLocationClick = (location) => {
    setLocationKey(prev => prev + 1); // Increment for new location
    
    // Update the map center based on the clicked location
    setMapCenter({
      lat: location.latitude, // Assuming the latitude is stored in 'latitude'
      lng: location.longitude, // Assuming the longitude is stored in 'longitude'
    });

    setZoomLevel(12); // Set the zoom level when a location is clicked

    // Optionally, show the marker and info window as well
    setSelectedLocation({
      name: location.location_name, // Assuming the location name is in 'location_name'
      lat: location.latitude,
      lng: location.longitude,
      address: location.location_address, // Assuming location address is stored in 'location_address'
    });

    setIsInfoWindowOpen1(true); // Open the info window
  };

  const handleAddLocation = () => {
    // Open the dialog and set form data
    setFormData({
      locationName: selectedLocation?.name || "",
      coordinates: `${selectedLocation?.lat}, ${selectedLocation?.lng}`,
      locationAddress: selectedLocation?.address || "",
      org_id: selectedLocation?.org_id || "",
      // Pre-fill from the Super Admin's current sub-org drilldown, if any -
      // without this, a new location added while viewing a sub-org was
      // always created at the org root (sub_org_id left blank), so it never
      // showed up under that sub-org, only under the parent Admin Org.
      sub_org_id: superAdminSelectedSubOrgId || "",
    });
    setIsEditing(false);
    setSelectedLocationId(null);
    setIsDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditClick = (location) => {
    setIsEditing(true);
    setFormData({
      locationName: location.location_name,
      locationAddress: location.location_address,
      coordinates: `${location.latitude}, ${location.longitude}`,
      radius: location.radius,
      org_id: location.org_id,
      sub_org_id: location.sub_org_id || "",
    });
    setSelectedLocationId(location.id);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = async (location) => {
    const payload = {
      entity_type: "location",
      parameter: [
        {
          columnName: "location_id",
          condition: "=",
          value: location,
        },
      ],
    };
    try {
      const deleteResponse = await deleteWorkScheduleAPI(payload);

      if (deleteResponse && deleteResponse.businessStatusCode === 2) {
        toast.success("Location deleted successfully");
        fetchMembers();
      } else {
        console.log("Unexpected response status:", deleteResponse.status);
      }
    } catch (error) {
      console.error("Error saving location:", error);
    }
  };

  const handleSave = async (e) => {
    const [latStr, lngStr] = formData.coordinates.split(",");

    // Build columns for create
    const columns = {
      location_name: formData.locationName,
      location_address: formData.locationAddress,
      latitude: latStr?.trim(),
      longitude: lngStr?.trim(),
      radius: formData.radius,
    };

    // When Super Admin is operating on a selected organisation, pass org_id in the
    // columns payload so the backend can override the JWT org with the target org.
    if (isSuperAdmin && superAdminSelectedOrgId) {
      columns.org_id = superAdminSelectedOrgId;
    }

    if (formData.sub_org_id) {
      columns.sub_org_id = formData.sub_org_id;
    }

    const requestBody = {
      entity_type: "location",
      comment: "comment added",
      columns,
    };

    // Build new_data for update operation
    const newData = {
      latitude: latStr?.trim(),
      location_id: selectedLocationId,
      location_name: formData.locationName,
      longitude: lngStr?.trim(),
      radius: formData.radius,
      status: "ACTIVE",
    };

    if (formData.sub_org_id) {
      newData.sub_org_id = formData.sub_org_id;
    }

    const requestBody1 = {
      entity_type: "location",
      comment: "comment added",
      old_data: {},
      new_data: newData,
    };

    try {
      let saveResponse;
      if (selectedLocationId) {
        saveResponse = await updateLocationAPI(selectedLocationId, requestBody1);
      } else {
        saveResponse = await saveLocationAPI(requestBody);
      }

      if (saveResponse && saveResponse.businessStatusCode === 2) {
        toast.success(selectedLocationId ? "Location updated successfully" : "Location added successfully");
        fetchMembers();
        setIsInfoWindowOpen(false);
        setIsDialogOpen(false);
      } else {
        toast.error(saveResponse?.message || "Failed to save location");
      }
    } catch (error) {
      console.error("Error saving location:", error);
      toast.error("An error occurred while saving the location");
    }
  };

  const handleCancel = () => {
    setIsDialogOpen(false); // Close the dialog
  };

  const isSaveDisabled = () => {
    return !(
      formData?.locationName?.trim() &&
      formData?.coordinates &&
      formData?.locationAddress &&
      formData?.radius
    );
  };

  // Handler for info icon click
  const handleInfoClick = (event, location) => {
    event.stopPropagation(); // Prevent triggering the location click
    setInfoAnchorEl(event.currentTarget);
    setInfoLocation(location);
  };

  // Handler for closing info popover
  const handleInfoClose = () => {
    setInfoAnchorEl(null);
    setInfoLocation(null);
  };

  const isInfoOpen = Boolean(infoAnchorEl);

  // Get sub-org name from sub_org_id
  const getSubOrgName = (subOrgId) => {
    if (!subOrgId) return null;
    const subOrg = subOrganizations.find(s => s.sub_org_id === subOrgId);
    return subOrg ? `${subOrg.sub_org_name} (${subOrg.sub_org_code})` : null;
  };

  // Show "Select Organization" message if Super Admin hasn't selected an org
  if (isSuperAdmin && !superAdminSelectedOrgId) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen" style={{ backgroundColor: "#EFF0F4" }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Locations</h1>
            <p className="text-sm text-gray-1000">Super Admin View</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center bg-white rounded-lg shadow-sm p-12 mt-8">
          <svg
            className="w-24 h-24 text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Select an Organization</h2>
          <p className="text-gray-1000 text-center max-w-md">
            Please select an organization from the dropdown in the header to view and manage its locations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid m-0 p-0">
      <div className="row" >
        {/* Search Input */}
        <div
          className="col-12 col-md-12 p-3 border-bottom text-black-50 "
          style={{ backgroundColor: "#EFF0F4", zIndex: 1 }}
        >
          {isMapsLoaded && (
            <Autocomplete
              onLoad={handleOnLoad}
              onPlaceChanged={handlePlaceChanged}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="text"
                  placeholder="Search place or enter coordinates (lat, lng)"
                  className="border border-dark-300 border-top-0 border-left-0 border-right-0 p-2 shadow-sm bg-white"
                  style={{ flex: 1, maxWidth: "500px" }}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                />
                {isCoordinates(searchInput) && (
                  <button
                    onClick={handleSearchClick}
                    style={{
                      backgroundColor: "#fd610a",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      padding: "8px 16px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "1000"
                    }}
                  >
                    Search
                  </button>
                )}
              </div>
            </Autocomplete>
          )}
        </div>

        <div className="col-12 col-md-12">
          <div className="row">
            <div
              className="col-12 col-md-3 text-center border-end text-black-50 bg-white shadow rounded"
              style={{ height: 'calc(100vh - 151px)', display: 'flex', flexDirection: 'column' }}
            >
              {!viewListData || viewListData.length === 0 ? (
                <div className="text-center p-3">
                  <p>Add locations to track time with GPS</p>
                  <img src={location} alt="Not found" className="w-100" />
                  <p className="text-secondary">
                    Search for a location on the map to begin
                  </p>
                </div>
              ) : (
                <div className="d-flex flex-column h-100">
                  {/* Header */}
                  <p className="mt-2 border-bottom text-black-50 fw-bold w-full items-center pb-3" style={{ flexShrink: 0 }}>
                    Added Locations
                  </p>

                  {/* Scrollable list */}
                  <div className="overflow-auto" style={{ flex: '1 1 auto', overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', padding: '0 8px' }}>
                    {viewListData.slice((locPage - 1) * locPageSize, locPage * locPageSize).map((item, index) => (
                      <div
                        key={index}
                        className="items-center px-1 py-2 shadow-sm"
                        style={{
                          padding: "15px",
                          backgroundColor: "#ffffff",
                          color: selectedLocation?.name === item.location_name ? "#FF8800" : "#333",
                          marginBottom: "1rem",
                          border: selectedLocation?.name === item.location_name ? "2px solid #FF8800" : "1px solid #eeeeee",
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          borderRadius: "20px",
                        }}
                        onClick={() => handleLocationClick(item)}
                      >
                        <MapPin
                          className="h-4 w-4 mr-2"
                          style={{ color: selectedLocation?.name === item.location_name ? "#FF8800" : "#666", flexShrink: 0 }}
                        />
                        <p
                          className="mb-0 flex-grow"
                          style={{
                            color: selectedLocation?.name === item.location_name ? "#FF8800" : "#333",
                            fontWeight: selectedLocation?.name === item.location_name ? "600" : "normal",
                            fontSize: "14px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.location_name}
                        </p>
                        <Tooltip title="View Details" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={(e) => handleInfoClick(e, item)}
                            sx={{ ml: 1, padding: '4px', '&:hover': { backgroundColor: 'rgba(253, 97, 10, 0.1)' } }}
                          >
                            <Info
                              className="h-4 w-4"
                              style={{ color: selectedLocation?.name === item.location_name ? "#FF8800" : "#999", width: '16px', height: '16px' }}
                            />
                          </IconButton>
                        </Tooltip>
                      </div>
                    ))}
                  </div>

                  {/* Pagination — fixed footer, identical to Tracking */}
                  {(() => {
                    const totalLocPages = Math.ceil(viewListData.length / locPageSize);
                    const WING = 1;
                    let start = Math.max(1, locPage - WING);
                    let end = Math.min(totalLocPages, locPage + WING);
                    if (end - start < 2) {
                      if (start === 1) end = Math.min(totalLocPages, start + 2);
                      else start = Math.max(1, end - 2);
                    }
                    const pageWindow = [];
                    for (let i = start; i <= end; i++) pageWindow.push(i);
                    const isFirst = locPage === 1;
                    const isLast = locPage === totalLocPages;
                    return (
                      <div className="lt-footer">
                        <span className="lt-page-info">{locPage} / {totalLocPages}</span>
                        <div className="lt-pagination">
                          <button className="lt-page-btn lt-page-nav" onClick={() => setLocPage(1)} disabled={isFirst} title="First page">«</button>
                          <button className="lt-page-btn lt-page-nav" onClick={() => setLocPage(locPage - 1)} disabled={isFirst} title="Previous page">‹</button>
                          {pageWindow[0] > 1 && (
                            <>
                              <button className="lt-page-btn" onClick={() => setLocPage(1)}>1</button>
                              {pageWindow[0] > 2 && <span className="lt-page-ellipsis">…</span>}
                            </>
                          )}
                          {pageWindow.map(n => (
                            <button key={n} className={`lt-page-btn${n === locPage ? ' lt-page-btn--active' : ''}`} onClick={() => setLocPage(n)}>{n}</button>
                          ))}
                          {pageWindow[pageWindow.length - 1] < totalLocPages && (
                            <>
                              {pageWindow[pageWindow.length - 1] < totalLocPages - 1 && <span className="lt-page-ellipsis">…</span>}
                              <button className="lt-page-btn" onClick={() => setLocPage(totalLocPages)}>{totalLocPages}</button>
                            </>
                          )}
                          <button className="lt-page-btn lt-page-nav" onClick={() => setLocPage(locPage + 1)} disabled={isLast} title="Next page">›</button>
                          <button className="lt-page-btn lt-page-nav" onClick={() => setLocPage(totalLocPages)} disabled={isLast} title="Last page">»</button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Location Info Popover */}
              <Popover
                    open={isInfoOpen}
                    anchorEl={infoAnchorEl}
                    onClose={handleInfoClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                    sx={{
                      '& .MuiPopover-paper': {
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        maxWidth: '300px',
                        overflow: 'hidden',
                      }
                    }}
                  >
                    {infoLocation && (
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: '#fd610a',
                            mb: 1.5,
                            pb: 1,
                            borderBottom: '1px solid #eee'
                          }}
                        >
                          {infoLocation.location_name}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#999', fontWeight: 1000 }}>
                              Address
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#333', fontSize: '13px' }}>
                              {infoLocation.location_address || 'N/A'}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="caption" sx={{ color: '#999', fontWeight: 1000 }}>
                              Coordinates
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#333', fontSize: '13px' }}>
                              {infoLocation.latitude?.toFixed(6)}, {infoLocation.longitude?.toFixed(6)}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="caption" sx={{ color: '#999', fontWeight: 1000 }}>
                              Radius
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#333', fontSize: '13px' }}>
                              {infoLocation.radius ? `${infoLocation.radius} m` : 'N/A'}
                            </Typography>
                          </Box>

                          {infoLocation.sub_org_id && getSubOrgName(infoLocation.sub_org_id) && (
                            <Box>
                              <Typography variant="caption" sx={{ color: '#999', fontWeight: 1000 }}>
                                Sub-Organization
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#333', fontSize: '13px' }}>
                                {getSubOrgName(infoLocation.sub_org_id)}
                              </Typography>
                            </Box>
                          )}

                          {infoLocation.created_at && (
                            <Box>
                              <Typography variant="caption" sx={{ color: '#999', fontWeight: 1000 }}>
                                Created
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#333', fontSize: '13px' }}>
                                {new Date(infoLocation.created_at).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Popover>
            </div>

            <div className="col-12 col-md-9  " style={{backgroundColor: "#eff0f4"}}>
              {isMapsLoaded ? <GoogleMap
                 className="shadow-sm rounded"
                  mapContainerStyle={{ height: "80vh", width: "100%",borderRadius:'3px' }}
                  center={mapCenter}
                  zoom={zoomLevel}
                  onClick={handleMapClick}
                >
                  {selectedLocation && (
                    <>
                      <Marker
                        key={`marker-${locationKey}`}
                        position={{
                          lat: selectedLocation.lat,
                          lng: selectedLocation.lng,
                        }}
                        onClick={() => setIsInfoWindowOpen(true)}
                      />

                      <Circle
                        key={`circle-${locationKey}`}
                        center={{
                          lat: selectedLocation.lat,
                          lng: selectedLocation.lng,
                        }}
                        radius={30}
                        options={{
                          strokeColor: "#FF0000",
                          strokeOpacity: 0.8,
                          strokeWeight: 2,
                          fillColor: "#fd610a",
                          fillOpacity: 0.35,
                        }}
                      />

                      {isInfoWindowOpen && (
                        <InfoWindow
                          position={{
                            lat: selectedLocation.lat,
                            lng: selectedLocation.lng,
                          }}
                          onCloseClick={() => setIsInfoWindowOpen(false)}
                        >
                          <div className="text-center">
                            <h6 className="fw-bold ">
                              {selectedLocation?.name}
                            </h6>
                            <span>{selectedLocation?.address}</span>
                            <br />
                            <div
                              className="btn mt-2"
                              style={{
                                height: "30px",
                                width: "120px",
                                fontSize: "10px",
                                backgroundColor: isSuspended ? "#ccc" : "#fd610a",
                                color: "white",
                                cursor: isSuspended ? "not-allowed" : "pointer",
                                opacity: isSuspended ? 0.6 : 1,
                              }}
                              onClick={isSuspended ? undefined : handleAddLocation}
                              title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                            >
                              Add New Location
                            </div>
                          </div>
                        </InfoWindow>
                      )}

                      {isInfoWindowOpen1 && (
                        <InfoWindow
                          position={{
                            lat: selectedLocation.lat,
                            lng: selectedLocation.lng,
                          }}
                          options={{ disableAutoPan: true }} // prevent re-centering when opening the window
                          cla
                        >
                          <div className="custom-info-window text-center mb-2">
                            <h6
                              className="fw-bold"
                              style={{ color: "#fd610a" }}
                            >
                              {selectedLocation?.name}
                            </h6>
                          </div>
                        </InfoWindow>
                      )}
                    </>
                  )}
                </GoogleMap> : (
                  <div style={{ height: "80vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}>
                    Loading map…
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Dialog for Adding New Location */}

        <Dialog
          open={isDialogOpen}
          onClose={handleCancel}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={false}
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              position: 'relative',
              zIndex: 1300,
              margin: '32px',
            },
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1299,
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            pt: 3, 
            px: 3,
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#1a1a1a',
            borderBottom: '1px solid #e0e0e0'
          }}>
            Add New Location
          </DialogTitle>
          <DialogContent sx={{ pt: 2.5, pb: 1.5, px: 3 }}>
            {/* Existing Fields */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '0.75rem', 
                color: '#666', 
                marginBottom: '6px',
                fontWeight: 1000
              }}>
                Location Name
              </label>
              <input
                autoFocus
                type="text"
                name="locationName"
                value={formData.locationName}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  height: '40px',
                  padding: '8px 12px',
                  fontSize: '0.875rem',
                  color: '#1a1a1a',
                  border: '1px solid #d0d0d0',
                  borderRadius: '4px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#fd610a'}
                onBlur={(e) => e.target.style.borderColor = '#d0d0d0'}
                onMouseOver={(e) => !e.target.matches(':focus') && (e.target.style.borderColor = '#999')}
                onMouseOut={(e) => !e.target.matches(':focus') && (e.target.style.borderColor = '#d0d0d0')}
              />
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '0.75rem', 
                color: '#999', 
                marginBottom: '6px',
                fontWeight: 1000
              }}>
                Coordinates
              </label>
              <input
                type="text"
                name="coordinates"
                value={formData.coordinates}
                onChange={handleInputChange}
                disabled
                style={{
                  width: '100%',
                  height: '40px',
                  padding: '8px 12px',
                  fontSize: '0.875rem',
                  color: '#555',
                  backgroundColor: '#f8f8f8',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  outline: 'none',
                  cursor: 'not-allowed',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '0.75rem', 
                color: '#999', 
                marginBottom: '6px',
                fontWeight: 1000
              }}>
                Location Address
              </label>
              <input
                type="text"
                name="locationAddress"
                value={formData.locationAddress}
                onChange={handleInputChange}
                disabled
                style={{
                  width: '100%',
                  height: '40px',
                  padding: '8px 12px',
                  fontSize: '0.875rem',
                  color: '#555',
                  backgroundColor: '#f8f8f8',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  outline: 'none',
                  cursor: 'not-allowed',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <FormControl fullWidth margin="none" sx={{ mb: 0 }}>
              <InputLabel
                sx={{ 
                  fontSize: "0.875rem", 
                  backgroundColor: "white",
                  px: 0.5,
                  color: "#666",
                  transform: 'translate(14px, 10px) scale(1)',
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)'
                  },
                  "&.Mui-focused": {
                    color: "#fd610a"
                  }
                }}
              >
                Select Radius
              </InputLabel>
              <Select
                name="radius"
                value={formData.radius || ''}
                onChange={handleInputChange}
                label="Select Radius"
                size="small"
                sx={{
                  fontSize: "0.875rem",
                  height: '40px',
                  "& .MuiSelect-select": {
                    padding: '8px 12px'
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#d0d0d0"
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#999"
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#fd610a",
                    borderWidth: '2px'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 1,
                      maxHeight: 240,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                      borderRadius: '8px',
                      "& .MuiMenuItem-root": {
                        fontSize: "0.95rem",
                        padding: "10px 16px",
                        "&:hover": {
                          backgroundColor: "#f5f5f5"
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#fff3e0",
                          "&:hover": {
                            backgroundColor: "#ffe0b2"
                          }
                        }
                      },
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
              >
                <MenuItem value={100}>100 m</MenuItem>
                <MenuItem value={200}>200 m</MenuItem>
                <MenuItem value={300}>300 m</MenuItem>
                <MenuItem value={400}>400 m</MenuItem>
                <MenuItem value={1000}>1000 m</MenuItem>
                <MenuItem value={600}>600 m</MenuItem>
                <MenuItem value={700}>700 m</MenuItem>
                <MenuItem value={800}>800 m</MenuItem>
                <MenuItem value={900}>900 m</MenuItem>
                <MenuItem value={1000}>1000 m</MenuItem>
              </Select>
            </FormControl>

            {/* Sub-Organization dropdown - shown for ORG_ADMIN, SUPER_ADMIN */}
            {subOrganizations.length > 0 && ['ADMIN', 'SUPER_ADMIN'].includes(userRole) && (
              <FormControl fullWidth margin="normal" sx={{ mt: 2 }}>
                <InputLabel
                  sx={{
                    fontSize: "0.875rem",
                    backgroundColor: "white",
                    px: 0.5,
                    color: "#666",
                    transform: 'translate(14px, 10px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.75)'
                    },
                    "&.Mui-focused": {
                      color: "#fd610a"
                    }
                  }}
                >
                  Sub-Organization (Optional)
                </InputLabel>
                <Select
                  name="sub_org_id"
                  value={formData.sub_org_id || ''}
                  onChange={handleInputChange}
                  label="Sub-Organization (Optional)"
                  size="small"
                  sx={{
                    fontSize: "0.875rem",
                    height: '40px',
                    "& .MuiSelect-select": {
                      padding: '8px 12px'
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#d0d0d0"
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#999"
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#fd610a",
                      borderWidth: '2px'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 1,
                        maxHeight: 240,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        "& .MuiMenuItem-root": {
                          fontSize: "0.95rem",
                          padding: "10px 16px",
                          "&:hover": {
                            backgroundColor: "#f5f5f5"
                          },
                          "&.Mui-selected": {
                            backgroundColor: "#fff3e0",
                            "&:hover": {
                              backgroundColor: "#ffe0b2"
                            }
                          }
                        },
                      },
                    },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>No Sub-Organization</em>
                  </MenuItem>
                  {subOrganizations.map((subOrg) => (
                    <MenuItem key={subOrg.sub_org_id} value={subOrg.sub_org_id}>
                      {subOrg.sub_org_name} ({subOrg.sub_org_code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions sx={{ 
            px: 3, 
            py: 2.5, 
            gap: 1.5,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fafafa'
          }}>
            <ArthaCancelButton onClick={handleCancel} color="primary">
              Cancel
            </ArthaCancelButton>
            <ArthaContinueButton
              onClick={isSuspended ? undefined : handleSave}
              color="primary"
              disabled={isSaveDisabled() || isSuspended}
              style={isSuspended ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
            >
              Save
            </ArthaContinueButton>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default AddWorkLocation;
